// import { Select } from '@mui/material';
import Select, { components, DropdownIndicatorProps } from 'react-select';
import { Controller } from 'react-hook-form';
import MaskedInput from 'react-input-mask';
import CurrencyInput from 'react-currency-input-field';
import { FormGroup, Label, Input, FormText, FormFeedback } from 'reactstrap';
import states from '~/shared/states.json';
import { FaRegEye, FaRegEyeSlash } from 'react-icons/fa';
import { useState, useEffect } from 'react';
import { ar } from 'date-fns/locale';

export const DropDownIcon = () => {
    return (
        <svg
            xmlns='http://www.w3.org/2000/svg'
            viewBox='0 0 16 16'
            width='16'
            height='12'
        >
            <path
                fill='none'
                stroke='#3c3f46'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='m2 5 6 6 6-6'
            ></path>
        </svg>
    );
};

export const DropdownIndicator: React.FC<DropdownIndicatorProps> = (props) => {
    return (
        <components.DropdownIndicator {...props}>
            <DropDownIcon />
        </components.DropdownIndicator>
    );
};

export const colourStyles = {
    control: (styles, { isDisabled }) => ({
        ...styles,
        backgroundColor: isDisabled ? '#f1f0ef' : 'white',
        borderColor: '#0d0106 !important',
        minHeight: '3rem',
        ':hover': {
            backgroundColor: 'rgba(0, 174, 179, 0.10)',
        },
        ':focus': {
            // borderWidth: '3px',
            borderColor: '#01aeb4 !important',
            boxShadow: 'inset 0 0 0 2px #01aeb4 !important',
        },
        ':focus-within': {
            // borderWidth: '3px',
            borderColor: '#01aeb4 !important',
            boxShadow: 'inset 0 0 0 2px #01aeb4 !important',
        },
    }),
    valueContainer: (styles) => ({
        ...styles,
        padding: '0.5rem 1rem',
    }),
    placeholder: (styles, { isDisabled }) => ({
        ...styles,
        color: isDisabled ? '#808080' : 'black',
    }),
    dropdownIndicator: (styles, { isDisabled }) => ({
        ...styles,
        paddingRight: '15px',
        color: isDisabled ? '#808080' : 'black',
    }),
    clearIndicator: (styles) => ({
        ...styles,
        color: 'black',
    }),
    indicatorSeparator: (styles) => ({
        ...styles,
        width: '0',
    }),
    menuList: (styles) => ({
        ...styles,
        background: 'white',
        border: '1px solid #000',
        borderRadius: '4px',
    }),
    option: (styles, { isFocused, isSelected }) => ({
        ...styles,
        background: isFocused ? '#E5F6F9' : isSelected ? '#E5F6F9' : undefined,
        color: isFocused ? 'black' : isSelected ? 'black' : undefined,
        zIndex: 1,
    }),
    menu: (base) => ({
        ...base,
        zIndex: 1021,
        boxShadow: 'none',
    }),
};

export const currencyConfig = {
    locale: 'en-US',
    formats: {
        number: {
            USD: {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            },
        },
    },
};

export const stateOptions = Object.entries(states).map(
    ([accronym, label], i) => {
        return {
            label,
            value: accronym,
        };
    }
);

export const numberFormat = (value) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(value);

export const phoneRegExp =
    /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

export const PasswordInput = ({
    control,
    required = false,
    ariaRequired = false,
    name,
    label,
    size = 'md',
    helperText = '',
    validate = () => {},
    readOnly = false,
}) => {
    const [showPassword, setShowPassword] = useState(false);

    const togglePasswordVisibility = () => {
        setShowPassword((prevState) => !prevState);
    };

    let rules = {
        required: required
            ? { value: true, message: `${label} is required` }
            : false,
        minLength: 8,
    };

    if (name === 'password_confirmation') {
        rules['validate'] = validate;
    }

    return (
        <Controller
            name={name}
            control={control}
            render={({
                field: { onChange, value, onBlur },
                fieldState: { error, invalid },
            }) => {
                return (
                    <FormGroup key={`formgroup-${name}`} disabled={readOnly}>
                        {label && (
                            <Label for={name} className='text-start'>
                                {label}{' '}
                                {required ? (
                                    <span className='required'>*</span>
                                ) : (
                                    ''
                                )}
                            </Label>
                        )}
                        <div style={{ position: 'relative' }}>
                            <Input
                                id={`formgroup-input-${name}`}
                                type={showPassword ? 'text' : 'password'}
                                bsSize={size}
                                onChange={onChange}
                                value={value || ''}
                                onBlur={onBlur}
                                name={name}
                                invalid={invalid}
                                readOnly={readOnly}
                                required={required}
                                aria-required={ariaRequired}
                            />
                            <span
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    right: '10px',
                                    transform: 'translateY(-50%)',
                                    cursor: 'pointer',
                                }}
                                onClick={togglePasswordVisibility}
                            >
                                {showPassword ? (
                                    <FaRegEyeSlash size={22} />
                                ) : (
                                    <FaRegEye size={22} />
                                )}
                            </span>
                        </div>
                        {helperText && (
                            <div className='text-start'>
                                <FormText>{helperText}</FormText>
                            </div>
                        )}
                        {error && (
                            <FormFeedback
                                style={{ display: 'inherit' }}
                                className='text-start'
                            >
                                {error.message}
                            </FormFeedback>
                        )}
                    </FormGroup>
                );
            }}
            rules={rules}
        />
    );
};
export const TextInput = ({
    control,
    required = false,
    ariaRequired = false,
    name,
    label,
    type = 'text',
    size = 'md',
    helperText = '',
    placeholder = '',
    newRules = {},
    validate = () => {},
    readOnly = false,
    maxLength,
    step,
    min,
    max,
}) => {
    let rules = { ...newRules };
    let inputTextColor = '';
    rules['required'] = required
        ? { value: true, message: `${label} is required` }
        : null;
    if (type === 'email') {
        rules['pattern'] = {
            value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            message: 'Please enter a valid email',
        };
    }
    if (type === 'zip') {
        rules['pattern'] = {
            value: /(?<!\d)\d{5}(?!\d)/,
            message: 'Please enter a valid zip code',
        };
    }
    if (type === 'url') {
        rules['pattern'] = {
            value: /(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#()?&//=]*)/,
            message: 'Please enter a valid url',
        };
    }
    if (type === 'password') {
        rules['minLength'] = 8;
        if (name === 'password_confirmation') {
            console.log('add validate to password_confirmation');
            rules['validate'] = validate;
        }
    }
    // if (errors) {
    // }

    const activeElement = document.activeElement;

    return (
        <Controller
            name={name}
            control={control}
            render={({
                field: { onChange, value, onBlur },
                fieldState: { error, invalid },
            }) => {
                return (
                    <FormGroup key={`formgroup-${name}`} disabled={readOnly}>
                        {label && (
                            <Label for={name} className='text-start'>
                                {label}{' '}
                                {required ? (
                                    <span className='required'>*</span>
                                ) : (
                                    ''
                                )}
                            </Label>
                        )}
                        <Input
                            style={{ color: inputTextColor }}
                            id={`formgroup-input-${name}`}
                            type={type}
                            bsSize={size}
                            onChange={onChange}
                            label={label}
                            value={value || ''}
                            onBlur={onBlur}
                            name={name}
                            // valid={!invalid && value !== ''}
                            invalid={invalid}
                            autoFocus={
                                activeElement.id === `formgroup-input-${name}`
                            }
                            readOnly={readOnly}
                            required={required}
                            aria-required={ariaRequired}
                            placeholder={placeholder}
                            maxLength={maxLength}
                            step={step}
                            min={min}
                            max={max}
                        />
                        {helperText && (
                            <div className='text-start'>
                                <FormText
                                    dangerouslySetInnerHTML={{
                                        __html: helperText,
                                    }}
                                />
                            </div>
                        )}
                        {error && (
                            <FormFeedback
                                style={{ display: 'inherit' }}
                                className='text-start'
                            >
                                {error.message}
                            </FormFeedback>
                        )}
                    </FormGroup>
                );
            }}
            rules={rules}
        />
    );
};

const DollarInputInner = ({
    field,
    invalid,
    name,
    label,
    helperText = '',
    readOnly = false,
    placeholder = '$0.00',
    required = false,
    ariaRequired = false,
    onValueSync,
}: any) => {
    const [inputValue, setInputValue] = useState<string>('');

    // Sync from external value changes (e.g., form reset)
    useEffect(() => {
        if (field?.value === undefined || field?.value === null) {
            setInputValue('');
        } else {
            setInputValue(String(field.value));
        }
    }, [field?.value]);

    return (
        <FormGroup>
            <Label for={name}>
                {label} {required ? <span className='required'>*</span> : ''}
            </Label>
            <CurrencyInput
                id={name}
                name={name}
                className={`form-control ${invalid ? 'is-invalid' : ''}`}
                prefix='$'
                decimalsLimit={2}
                decimalScale={2}
                decimalSeparator='.'
                groupSeparator=','
                allowDecimals={true}
                value={inputValue}
                placeholder={placeholder}
                onValueChange={(value, _name, values) => {
                    setInputValue(value ?? '');
                    const next = values?.float ?? null;
                    field?.onChange?.(next);
                    onValueSync?.(next);
                }}
                onFocus={(e) => {
                    // Select-all so user can overwrite without backspacing
                    const target = e.target as HTMLInputElement;
                    setTimeout(() => target.select(), 0);
                }}
                allowNegativeValue={false}
                readOnly={readOnly}
                required={required}
                aria-required={ariaRequired}
            />
            {helperText && <FormText>{helperText}</FormText>}
            {invalid && (
                <FormFeedback style={{ display: 'inherit' }}>
                    {invalid?.message}
                </FormFeedback>
            )}
        </FormGroup>
    );
};

export const DollarInput = ({
    control,
    required = false,
    ariaRequired = false,
    name,
    label,
    size = 'md',
    helperText = '',
    readOnly = false,
    defaultValue = '',
    placeholder = '$0.00',
    setValue,
}) => {
    let rules = {};
    rules['required'] = required
        ? { value: true, message: `${label} is required` }
        : null;

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState }) => (
                <DollarInputInner
                    field={field}
                    invalid={fieldState.error}
                    name={name}
                    label={label}
                    helperText={helperText}
                    readOnly={readOnly}
                    placeholder={placeholder}
                    required={required}
                    ariaRequired={ariaRequired}
                    onValueSync={(next: any) => {
                        if (typeof setValue === 'function') {
                            try {
                                setValue(name, next, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                });
                            } catch {}
                        }
                    }}
                />
            )}
            rules={rules}
        />
    );
};

export const PhoneInput = ({
    control,
    required = false,
    ariaRequired = false,
    name,
    label,
    helperText = '',
    readOnly = false,
}) => {
    let rules = {};
    rules['required'] = required
        ? { value: true, message: `${label} is required` }
        : null;
    return (
        <FormGroup>
            <Controller
                name={name}
                control={control}
                render={({
                    field: { onChange, value, onBlur },
                    fieldState: { error, invalid },
                }) => {
                    return (
                        <FormGroup disabled={readOnly}>
                            <Label for={name}>
                                {label}{' '}
                                {required ? (
                                    <span className='required'>*</span>
                                ) : (
                                    ''
                                )}
                            </Label>
                            <MaskedInput
                                type='tel'
                                mask='999-999-9999'
                                onChange={onChange}
                                label={label}
                                value={value || ''}
                                onBlur={onBlur}
                                name={name}
                                className={
                                    invalid
                                        ? 'form-control is-invalid'
                                        : 'form-control'
                                }
                                required={required}
                                aria-required={ariaRequired}
                            />
                            {helperText && <FormText>{helperText}</FormText>}
                            {error && (
                                <FormFeedback style={{ display: 'inherit' }}>
                                    {error.message}
                                </FormFeedback>
                            )}
                        </FormGroup>
                    );
                }}
                rules={rules}
            />
        </FormGroup>
    );
};

export const CheckboxInput = ({
    control,
    type = 'checkbox',
    required = false,
    ariaRequired = false,
    disabled = false,
    id,
    name,
    label,
    helperText = '',
    value,
    // onClick = () => {},
}) => {
    return (
        <FormGroup check className='mb-3'>
            <Controller
                name={name}
                control={control}
                render={({
                    field: { onChange, value: fieldValue, name },
                    fieldState: { error, invalid },
                }) => {
                    return (
                        <FormGroup label={label}>
                            <Label
                                check
                                onChange={(e) => {
                                    if (type === 'radio') {
                                        onChange(value);
                                    } else {
                                        onChange(e.target.checked);
                                    }
                                }}
                            >
                                <Input
                                    type={type}
                                    id={id || name}
                                    name={name}
                                    value={value}
                                    checked={
                                        type === 'radio'
                                            ? fieldValue === value
                                            : fieldValue
                                    }
                                    disabled={disabled}
                                    aria-required={ariaRequired}
                                    onChange={(e) => {
                                        if (type === 'radio') {
                                            onChange(value);
                                        } else {
                                            onChange(e.target.checked);
                                        }
                                    }}
                                />{' '}
                                {label}
                            </Label>
                            {helperText && (
                                <div>
                                    <FormText>{helperText}</FormText>
                                </div>
                            )}
                            {error && (
                                <FormFeedback>{error.message}</FormFeedback>
                            )}
                        </FormGroup>
                    );
                }}
                rules={{ required }}
            />
        </FormGroup>
    );
};

export const SelectInput = ({
    control,
    required = false,
    ariaReauired = false,
    name,
    label,
    defaultLabel = null,
    options,
    getOptionLabel = (option) => option.label,
    formatOptionLabel = ({ label }) => label,
    instructions = null,
    readOnly = false,
}) => {
    let rules = {};
    rules['required'] = required
        ? { value: true, message: `${label} is required` }
        : null;
    return (
        <FormGroup>
            <Controller
                name={name}
                control={control}
                render={({
                    field: { onChange, value, name },
                    fieldState: { error, invalid },
                }) => {
                    return (
                        <FormGroup label={label} id={name} disabled={readOnly}>
                            <Label for={name}>
                                {label}{' '}
                                {required ? (
                                    <span className='required'>*</span>
                                ) : (
                                    ''
                                )}
                            </Label>
                            <Select
                                styles={colourStyles}
                                components={{ DropdownIndicator }}
                                options={options}
                                getOptionLabel={getOptionLabel}
                                formatOptionLabel={formatOptionLabel}
                                onChange={(opt) => {
                                    onChange(opt.value);
                                }}
                                value={
                                    value &&
                                    options.find((opt) => opt.value === value)
                                }
                                required={required}
                                aria-required={ariaReauired}
                                classNames={{
                                    control: (state) =>
                                        invalid
                                            ? 'invalid-input is-invalid'
                                            : '',
                                }}
                            />
                            {error && (
                                <FormFeedback style={{ display: 'inherit' }}>
                                    {error.message}
                                </FormFeedback>
                            )}
                        </FormGroup>
                    );
                }}
                rules={rules}
            />
        </FormGroup>
    );
};

// TODO - finish onChange()
export const ChooseFileInput = ({
    control,
    required = false,
    ariaRequired = false,
    name,
    label,
    errors,
}) => {
    return (
        <FormGroup>
            <Controller
                name={name}
                control={control}
                render={({ field: { onChange, value, name } }) => {
                    // console.log('CHOOSEFILE', current.files);

                    return (
                        <FormGroup label={label} id={name}>
                            <Label for={name}>
                                {label}{' '}
                                {required ? (
                                    <span className='required'>*</span>
                                ) : (
                                    ''
                                )}
                            </Label>
                            <Input
                                type='file'
                                name='file'
                                id={name}
                                value={value}
                                onChange={(e) => {
                                    console.log(
                                        'ChooseFileInput onChange() value=',
                                        value
                                    );
                                    console.log('files', e.target.files[0]);
                                }}
                                required={required}
                                aria-required={ariaRequired}
                            />
                        </FormGroup>
                    );
                }}
                rules={{ required }}
            />
            {errors && <FormText>{errors}</FormText>}
        </FormGroup>
    );
};
